/**
 * AI Recruit - Bulk Apply Resumes to Job
 *
 * Interactive duplicate confirmation (identical to single upload) WITHOUT the
 * recruiter being trapped in a modal:
 *
 *   files → duplicate check (3 at a time)
 *         ├─ no duplicate  → uploaded straight away, never waits for a human
 *         └─ duplicate      → parked as "Needs your decision"; the rest of the
 *                             batch KEEPS FLOWING. The decision is taken in the
 *                             exact same DuplicateDetectionModal single upload
 *                             uses (Cancel / Upload and Match Anyway).
 *
 * While the dialog is open, a pending decision opens that popup immediately —
 * so it feels like single upload. While MINIMIZED, it does not steal focus:
 * the tray turns amber, shows "N need your decision", and an actionable toast
 * offers to reopen. Nothing is decided behind the recruiter's back.
 *
 * The single-resume "Apply Resume" flow on the job page is untouched.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  CloudArrowUpIcon,
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  ClockIcon,
  MinusIcon,
  ArrowsPointingOutIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'

import { resumesApi } from '@/services/api'
import { fetchSources } from '@/services/sourceApi'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import SourceSelector from '@/components/common/SourceSelector'
import DuplicateDetectionModal, { type DuplicateDetectionData } from '@/components/resume/DuplicateDetectionModal'

interface BulkApplyResumesModalProps {
  isOpen: boolean
  onClose: () => void
  jobId: string
  jobTitle?: string
  /** Called once the batch has finished so the caller can refresh its results. */
  onCompleted?: () => void
}

interface BulkUploadConfig {
  max_files: number
  max_file_size_mb: number
  allowed_file_types: string[]
}

// Used only until GET /resumes/bulk-upload-config responds (and if it fails).
// Kept in sync with core/config.py's own defaults so this brief loading-state
// render shows the real limits instead of flashing a stale number.
const FALLBACK_CONFIG: BulkUploadConfig = {
  max_files: 15,
  max_file_size_mb: 50,
  allowed_file_types: ['pdf', 'doc', 'docx', 'txt'],
}

// How many files are checked/uploaded at once. Duplicate detection runs a Claude
// contact-extraction call per file, so this balances a fast batch against
// stampeding the LLM with parallel calls.
const WORK_CONCURRENCY = 5

/** A staged file plus its own source. Empty sourceId means "inherit the main source". */
interface FileEntry {
  key: string
  file: File
  sourceId: string
}

type RowPhase =
  | 'queued'
  | 'checking'
  | 'needs_decision'
  | 'uploading'
  | 'processing'
  | 'matched'
  | 'failed'
  | 'skipped'
  | 'cancelled'

interface FileRow {
  key: string
  file: File
  /** Resolved source actually sent for this file. */
  sourceId: string
  phase: RowPhase
  /** Payload for the shared duplicate popup, when this file needs a decision. */
  duplicateData?: DuplicateDetectionData
  duplicateCount?: number
  wasDuplicate?: boolean
  resumeId?: string
  candidate?: string
  email?: string
  phone?: string
  experience?: number
  isDuplicate?: boolean
  duplicateConfidence?: number
  failureReason?: string
}

type FilterKey = 'all' | 'processing' | 'decision' | 'matched' | 'failed' | 'skipped'

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const PHASE_PILL: Record<RowPhase, { label: string; cls: string; spin?: boolean; icon?: React.ReactNode }> = {
  queued: { label: 'Queued', cls: 'bg-gray-50 text-gray-500 border-gray-200', icon: <ClockIcon className="h-4 w-4" /> },
  checking: { label: 'Checking duplicate', cls: 'bg-blue-50 text-blue-700 border-blue-200', spin: true },
  needs_decision: { label: 'Needs your decision', cls: 'bg-amber-50 text-amber-800 border-amber-300', icon: <ExclamationTriangleIcon className="h-4 w-4" /> },
  uploading: { label: 'Uploading', cls: 'bg-blue-50 text-blue-700 border-blue-200', spin: true },
  // This popup's job for a row ends the moment it's inserted into the
  // database -- it does NOT track AI analysis/matching from here on (see the
  // job's own match results table for that). So 'processing' is a TERMINAL
  // state for this popup's display purposes, not a live "still working" one
  // -- no spinner, a done-looking checkmark instead.
  processing: { label: 'Uploaded', cls: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircleIcon className="h-4 w-4" /> },
  matched: { label: 'Matched', cls: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircleIcon className="h-4 w-4" /> },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 border-red-200', icon: <ExclamationTriangleIcon className="h-4 w-4" /> },
  skipped: { label: 'Skipped', cls: 'bg-gray-100 text-gray-500 border-gray-200', icon: <XMarkIcon className="h-4 w-4" /> },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500 border-gray-200', icon: <XMarkIcon className="h-4 w-4" /> },
}

/** Phases that mean "still working" (excludes needs_decision, tracked
 * separately, and 'processing' -- deliberately excluded, see
 * PHASE_PILL.processing above). */
const IN_FLIGHT_PHASES: RowPhase[] = ['queued', 'checking', 'uploading']

const PhasePill: React.FC<{ phase: RowPhase }> = ({ phase }) => {
  const { label, cls, spin, icon } = PHASE_PILL[phase]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${cls}`}>
      {spin ? <LoadingSpinner size="sm" /> : icon}
      {label}
    </span>
  )
}

const BulkApplyResumesModal: React.FC<BulkApplyResumesModalProps> = ({
  isOpen,
  onClose,
  jobId,
  jobTitle,
  onCompleted,
}) => {
  const [source_id, setSourceId] = useState<string>('')
  const [useSameSourceForAll, setUseSameSourceForAll] = useState(true)
  const [entries, setEntries] = useState<FileEntry[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [started, setStarted] = useState(false)
  const [rows, setRows] = useState<FileRow[]>([])
  const [trackedIds, setTrackedIds] = useState<string[]>([])
  const [filter, setFilter] = useState<FilterKey>('all')
  const [minimized, setMinimized] = useState(false)
  /** Row key whose duplicate popup is currently on screen. */
  const [activeDecisionKey, setActiveDecisionKey] = useState<string | null>(null)

  const runIdRef = useRef(0)
  // Stops the pool starting new work. Separate from runIdRef so in-flight
  // uploads still record the resume_id the server already created.
  const stoppedRef = useRef(false)
  const notifiedRef = useRef(false)
  const onCompletedRef = useRef(onCompleted)
  useEffect(() => { onCompletedRef.current = onCompleted }, [onCompleted])
  // Keys we've already raised a toast for, so each duplicate alerts once.
  const alertedKeysRef = useRef<Set<string>>(new Set())
  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['bulk-upload-config'],
    queryFn: async () => (await resumesApi.getBulkUploadConfig()).data as BulkUploadConfig,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })
  const config = configQuery.data ?? FALLBACK_CONFIG
  const maxFiles = config.max_files
  const maxBytes = config.max_file_size_mb * 1024 * 1024
  const allowedTypes = config.allowed_file_types
  const acceptAttr = allowedTypes.map((t) => `.${t}`).join(',')

  // Shares SourceSelector's query key so all dropdowns come from one request.
  const sourcesQuery = useQuery({
    queryKey: ['sources', 'resume'],
    queryFn: () => fetchSources('resume'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })
  const sources = useMemo(
    () => (sourcesQuery.data ? [...sourcesQuery.data].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [sourcesQuery.data]
  )
  const sourceName = useCallback(
    (id?: string) => sources.find((s) => s.id === id)?.name || '—',
    [sources]
  )

  const resetState = useCallback(() => {
    setSourceId('')
    setUseSameSourceForAll(true)
    setEntries([])
    setDragOver(false)
    setStarted(false)
    setRows([])
    setTrackedIds([])
    setFilter('all')
    setMinimized(false)
    setActiveDecisionKey(null)
    notifiedRef.current = false
    stoppedRef.current = false
    alertedKeysRef.current = new Set()
  }, [])

  useEffect(() => {
    if (isOpen) {
      resetState()
      runIdRef.current++
    }
  }, [isOpen, resetState])

  useEffect(() => {
    if (!isOpen) runIdRef.current++
  }, [isOpen])

  const updateRow = useCallback((key: string, patch: Partial<FileRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }, [])

  // ---- File selection ----
  const addFiles = useCallback((incoming: FileList | File[]) => {
    const arr = Array.from(incoming)
    setEntries((prev) => {
      const seen = new Set(prev.map((e) => e.key))
      const next = [...prev]
      let skippedType = 0
      let skippedBig = 0
      for (const f of arr) {
        const ext = f.name.split('.').pop()?.toLowerCase()
        if (!ext || !allowedTypes.includes(ext)) { skippedType++; continue }
        if (f.size > maxBytes) { skippedBig++; continue }
        const key = `${f.name}:${f.size}`
        if (seen.has(key)) continue
        seen.add(key)
        next.push({ key, file: f, sourceId: '' })
      }
      if (skippedType) toast.error(`${skippedType} file${skippedType === 1 ? '' : 's'} skipped — unsupported type`)
      if (skippedBig) toast.error(`${skippedBig} file${skippedBig === 1 ? '' : 's'} skipped — over ${config.max_file_size_mb}MB`)
      if (next.length > maxFiles) {
        toast.error(`You can apply up to ${maxFiles} resumes at a time — only the first ${maxFiles} were kept`)
        return next.slice(0, maxFiles)
      }
      return next
    })
  }, [allowedTypes, maxBytes, maxFiles, config.max_file_size_mb])

  const removeFile = useCallback(
    (key: string) => setEntries((prev) => prev.filter((e) => e.key !== key)),
    []
  )
  const setEntrySource = useCallback((key: string, sourceId: string) => {
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, sourceId } : e)))
  }, [])
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false)
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
  }, [addFiles])
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files)
    e.target.value = ''
  }, [addFiles])

  // Build the EXISTING modal's data shape — identical to how single apply does it.
  const buildDuplicateData = useCallback((file: File, data: any): DuplicateDetectionData => ({
    has_duplicates: true,
    extracted_info: {
      candidate_name: data.search_criteria?.name || null,
      candidate_email: data.search_criteria?.email || null,
      candidate_phone: data.search_criteria?.phone || null,
      file_name: file.name,
      file_size: file.size,
    },
    duplicate_candidates: data.results || [],
    total_duplicates_found: data.total_found || 0,
    popup_config: {
      title: 'Potential Duplicate Resumes Found',
      message: `Found ${data.total_found} potential duplicate(s) for this candidate.`,
      show_upload_button: true,
      upload_endpoint: '/api/v2/resumes/upload-and-match',
      cancel_text: 'Cancel',
      upload_text: 'Upload and Match Anyway',
    },
  }), [])

  // Ingest one file through the SAME endpoint single apply uses.
  const uploadRow = useCallback(async (row: FileRow, runId: number) => {
    if (runIdRef.current !== runId) return
    updateRow(row.key, { phase: 'uploading', failureReason: undefined })
    try {
      const fd = new FormData()
      fd.append('file', row.file)
      fd.append('source_id', row.sourceId)
      const res = await resumesApi.uploadAndMatch(jobId, fd)
      if (runIdRef.current !== runId) return
      const data = res.data as any
      const id = data.resume_id as string
      updateRow(row.key, {
        phase: 'processing',
        resumeId: id,
        isDuplicate: !!data.is_duplicate,
        duplicateConfidence: data.duplicate_confidence ?? undefined,
        candidate: data.candidate_name && data.candidate_name !== 'Processing...'
          ? data.candidate_name
          : row.candidate,
        // Available immediately from the pre-upload Claude contact-check --
        // unlike experience/score, which only exist after full AI
        // analysis/matching (this popup no longer waits for that).
        email: data.extracted_email || row.email,
        phone: data.extracted_phone || row.phone,
      })
      setTrackedIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
      // Refresh both the job's match results and the general candidates
      // list right away so this row shows up as Queued/Processing
      // immediately -- don't wait for the later "everything's done
      // analyzing/matching" invalidate below, which can be minutes away.
      queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] })
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
    } catch (e: any) {
      if (runIdRef.current !== runId) return
      updateRow(row.key, { phase: 'failed', failureReason: e.response?.data?.detail || 'Upload failed' })
      // The backend already logged this rejection for the notification
      // history (see utils/upload_failure_log.py) -- refresh it so the
      // failure shows up there right away instead of only after a reload.
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
    }
  }, [updateRow, jobId, queryClient])

  /**
   * Check one file, then either upload it or park it for a human decision.
   * Crucially this does NOT wait for the decision — the pool moves straight on
   * to the next file, so one duplicate never holds up the whole batch.
   */
  const processOne = useCallback(async (row: FileRow, runId: number) => {
    if (runIdRef.current !== runId) return
    updateRow(row.key, { phase: 'checking', failureReason: undefined })

    let data: any = { total_found: 0 }
    try {
      const fd = new FormData()
      fd.append('file', row.file)
      data = (await resumesApi.searchDuplicates(fd)).data
    } catch {
      data = { total_found: 0 } // dup-check failure → proceed, mirroring single apply
    }
    if (runIdRef.current !== runId) return

    if (data.total_found > 0) {
      updateRow(row.key, {
        phase: 'needs_decision',
        wasDuplicate: true,
        duplicateData: buildDuplicateData(row.file, data),
        duplicateCount: data.total_found,
      })
      return
    }
    await uploadRow(row, runId)
  }, [updateRow, buildDuplicateData, uploadRow])

  // Bounded-concurrency pool over the batch.
  const runBatch = useCallback(async (list: FileRow[], runId: number) => {
    let cursor = 0
    const worker = async () => {
      while (cursor < list.length) {
        if (stoppedRef.current || runIdRef.current !== runId) return
        const row = list[cursor++]
        await processOne(row, runId)
      }
    }
    await Promise.all(Array.from({ length: Math.min(WORK_CONCURRENCY, list.length) }, worker))
  }, [processOne])

  const resolvedSourceFor = useCallback(
    (entry: FileEntry) => (useSameSourceForAll ? source_id : entry.sourceId || source_id),
    [useSameSourceForAll, source_id]
  )

  const entriesMissingSource = useMemo(
    () => entries.filter((e) => !resolvedSourceFor(e)),
    [entries, resolvedSourceFor]
  )

  const startBatch = useCallback(() => {
    if (!entries.length) { toast.error('Please add at least one resume'); return }
    if (useSameSourceForAll && !source_id) {
      toast.error('Please select a source first')
      return
    }
    if (entriesMissingSource.length) {
      toast.error(
        `${entriesMissingSource.length} resume(s) still need a source — set one per resume, or tick "Use this source for all resume(s)".`
      )
      return
    }
    const myRun = ++runIdRef.current
    stoppedRef.current = false
    alertedKeysRef.current = new Set()
    const initial: FileRow[] = entries.map((e, i) => ({
      key: `${e.file.name}-${e.file.size}-${i}`,
      file: e.file,
      sourceId: resolvedSourceFor(e),
      phase: 'queued',
    }))
    notifiedRef.current = false
    setRows(initial)
    setStarted(true)
    void runBatch(initial, myRun)
  }, [entries, useSameSourceForAll, source_id, entriesMissingSource, runBatch, resolvedSourceFor])

  // ---- Duplicate decisions ----
  const pendingDecisions = useMemo(() => rows.filter((r) => r.phase === 'needs_decision'), [rows])
  const activeDecisionRow = useMemo(
    () => rows.find((r) => r.key === activeDecisionKey && r.phase === 'needs_decision') || null,
    [rows, activeDecisionKey]
  )

  // While the dialog is OPEN, surface the next decision immediately — this is
  // what makes it feel like single upload. While minimized we never steal focus.
  useEffect(() => {
    if (!started || minimized || activeDecisionKey) return
    const next = pendingDecisions[0]
    if (next) setActiveDecisionKey(next.key)
  }, [started, minimized, activeDecisionKey, pendingDecisions])

  // Alert once per duplicate while minimized, with a one-click way back in.
  useEffect(() => {
    if (!minimized) return
    for (const row of pendingDecisions) {
      if (alertedKeysRef.current.has(row.key)) continue
      alertedKeysRef.current.add(row.key)
      toast(
        (t) => (
          <div className="flex items-start gap-3">
            <div className="text-sm">
              <p className="font-medium text-gray-900">Duplicate found</p>
              <p className="text-gray-600 mt-0.5 max-w-xs truncate" title={row.file.name}>{row.file.name}</p>
            </div>
            <button
              onClick={() => { toast.dismiss(t.id); setMinimized(false) }}
              className="px-2.5 py-1 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 whitespace-nowrap"
            >
              Review
            </button>
          </div>
        ),
        { icon: '⚠️', duration: 10000 }
      )
    }
  }, [minimized, pendingDecisions])

  const decide = useCallback((key: string, action: 'upload' | 'skip') => {
    const row = rows.find((r) => r.key === key)
    setActiveDecisionKey(null)
    if (!row) return
    if (action === 'upload') {
      void uploadRow({ ...row, duplicateData: undefined }, runIdRef.current)
    } else {
      updateRow(key, { phase: 'skipped' })
    }
  }, [rows, uploadRow, updateRow])

  // ---- Status polling ----
  const statusQuery = useQuery({
    queryKey: ['bulk-apply-status', jobId, trackedIds],
    queryFn: async () => {
      const res = await resumesApi.getBulkUploadAndMatchStatus(jobId, trackedIds)
      return res.data as { processing: number; items: any[] }
    },
    enabled: isOpen && started && trackedIds.length > 0,
    refetchInterval: (query) => {
      const d = query.state.data as { processing: number } | undefined
      return d && d.processing > 0 ? 5000 : false
    },
  })

  // This popup no longer reflects AI-analysis/matching PROGRESS on screen
  // (see PHASE_PILL.processing above) — a row that reached 'processing'
  // stays showing "Uploaded" for good, phase never advances to 'matched',
  // and score/gate-status are gone entirely; the job's own match results
  // table is where that progress actually gets tracked now.
  //
  // Experience is the one exception, deliberately: unlike score/gate-status
  // (only meaningful once THIS job's matching has run), years-of-experience
  // is a property of the résumé itself, already known once its own analysis
  // finishes — showing it here isn't the popup racing the list to report a
  // match result, it's just not leaving a field blank once the data exists.
  //
  // The poll itself keeps running in the background regardless, purely to
  // know WHEN analysis/matching has finished, so other open views (the
  // match results table) refresh without the user having to reload.
  useEffect(() => {
    const items = statusQuery.data?.items
    if (items) {
      setRows((prev) => prev.map((r) => {
        if (!r.resumeId || r.experience != null) return r
        const s = items.find((i: any) => i.id === r.resumeId)
        return s?.years_of_experience != null ? { ...r, experience: s.years_of_experience } : r
      }))
    }
    const stillProcessing = (statusQuery.data?.processing ?? 0) > 0
    if (stillProcessing) {
      notifiedRef.current = false
    } else if (statusQuery.data && !notifiedRef.current) {
      notifiedRef.current = true
      queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] })
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
      onCompletedRef.current?.()
    }
  }, [statusQuery.data, queryClient, jobId])

  // ---- Derived counts ----
  const counts = useMemo(() => {
    // `inserting` is genuinely still in-flight (checking/uploading) — this
    // popup still has work to do. `uploaded` is 'processing': successfully
    // inserted, terminal for THIS popup's display (see PHASE_PILL.processing).
    // `matched` stays for structural completeness but is effectively unused
    // now — nothing sets phase to 'matched' here anymore.
    const c = { queued: 0, inserting: 0, uploaded: 0, decision: 0, matched: 0, failed: 0, skipped: 0, cancelled: 0, duplicates: 0 }
    for (const r of rows) {
      if (r.phase === 'queued') c.queued++
      else if (r.phase === 'checking' || r.phase === 'uploading') c.inserting++
      else if (r.phase === 'processing') c.uploaded++
      else if (r.phase === 'needs_decision') c.decision++
      else if (r.phase === 'matched') c.matched++
      else if (r.phase === 'failed') c.failed++
      else if (r.phase === 'skipped') c.skipped++
      else if (r.phase === 'cancelled') c.cancelled++
      if (r.isDuplicate) c.duplicates++
    }
    return c
  }, [rows])

  // "Settled" and "busy" are about THIS popup's own job -- duplicate-check
  // and insert into the database -- not the AI analysis/matching that
  // follows. A row that reached 'processing' has already been handed off
  // to celery; this popup doesn't wait for celery to finish, it only waits
  // until nothing is still queued, being checked/uploaded, or waiting on a
  // duplicate decision.
  const settled = rows.length - counts.queued - counts.inserting - counts.decision
  const busy = counts.queued > 0 || counts.inserting > 0 || counts.decision > 0
  const uploadsDispatched = rows.some((r) => !!r.resumeId)

  /**
   * Work a reload would actually destroy: files not yet handed to the server
   * (queued / being checked / mid-request) plus any awaiting a duplicate
   * decision. Deliberately EXCLUDES 'processing' — those are already on the
   * server and finish regardless, so warning about them would be a false alarm.
   */
  const atRiskCount = useMemo(
    () => rows.filter((r) => ['queued', 'checking', 'uploading', 'needs_decision'].includes(r.phase)).length,
    [rows]
  )

  // Warn on reload / tab close while resumes would be lost. Browsers only allow
  // their native "Reload site?" prompt here — a custom modal can't render during
  // teardown — so the in-app notice below carries the explanation.
  // Same pattern as InterviewRecordingPage.
  useEffect(() => {
    if (atRiskCount === 0) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Required for the native prompt to appear in some browsers.
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [atRiskCount])

  const handleStopRemaining = useCallback(() => {
    stoppedRef.current = true
    const stopped = rows.filter((r) => r.phase === 'queued').length
    setRows((prev) => prev.map((r) => (r.phase === 'queued' ? { ...r, phase: 'cancelled' } : r)))
    toast(`Stopped ${stopped} queued resume(s). Anything already uploaded will finish.`, {
      icon: '🛑', duration: 5000,
    })
  }, [rows])

  const retryRow = useCallback((row: FileRow) => {
    // A résumé record already exists for this row (the original attempt got
    // past upload and only failed during analysis/matching) -- retry THAT
    // SAME record instead of re-uploading the file, which would go through
    // duplicate detection again and create a second résumé for one person.
    if (row.resumeId) {
      updateRow(row.key, { phase: 'processing', failureReason: undefined })
      resumesApi.reprocess(row.resumeId, jobId).catch((e: any) => {
        updateRow(row.key, { phase: 'failed', failureReason: e.response?.data?.detail || 'Retry failed' })
      })
      setTrackedIds((prev) => (prev.includes(row.resumeId!) ? prev : [...prev, row.resumeId!]))
      // Polling stops itself once nothing is processing (refetchInterval
      // returns false) -- kick it awake now rather than waiting for some
      // other row to also be in flight.
      void statusQuery.refetch()
      return
    }
    // No résumé record was ever created (the upload call itself failed) --
    // nothing exists yet to retry in place, so this is the only real option.
    void processOne(row, runIdRef.current)
  }, [processOne, updateRow, jobId, statusQuery])

  const openCandidate = (id?: string) => { if (id) window.open(`/resumes/${id}`, '_blank', 'noopener') }

  const handleCloseAll = useCallback(() => {
    const queued = rows.filter((r) => r.phase === 'queued').length
    const awaiting = counts.decision
    // Deliberately reads statusQuery's live `processing` count, NOT
    // rows.filter(phase === 'processing') -- that phase is frozen the
    // moment a row is inserted (see PHASE_PILL.processing) and never
    // updates again on screen, so it would still count a resume that
    // finished analyzing/matching minutes ago as "in flight" here.
    const inFlight = statusQuery.data?.processing ?? 0
    const lost = queued + awaiting

    if (lost > 0) {
      const parts: string[] = []
      if (queued > 0) parts.push(`${queued} resume(s) haven't started uploading`)
      if (awaiting > 0) parts.push(`${awaiting} are waiting for your duplicate decision`)
      const ok = window.confirm(
        `${parts.join(' and ')} — these will be cancelled.` +
        (inFlight > 0 ? `\n\n${inFlight} already uploaded will keep analyzing in the background.` : '') +
        `\n\nClose anyway?`
      )
      if (!ok) return
    } else if (inFlight > 0) {
      toast(`${inFlight} resume(s) will keep analyzing in the background.`, { icon: '⏳', duration: 4000 })
    }

    stoppedRef.current = true
    runIdRef.current++
    if (started) {
      queryClient.invalidateQueries({ queryKey: ['comparison-results', jobId] })
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
    }
    resetState()
    onClose()
  }, [rows, counts.decision, started, resetState, onClose, queryClient, jobId, statusQuery.data])

  const filteredRows = useMemo(() => {
    if (filter === 'all') return rows
    if (filter === 'processing') return rows.filter((r) => IN_FLIGHT_PHASES.includes(r.phase))
    if (filter === 'decision') return rows.filter((r) => r.phase === 'needs_decision')
    if (filter === 'matched') return rows.filter((r) => r.phase === 'processing' || r.phase === 'matched')
    if (filter === 'failed') return rows.filter((r) => r.phase === 'failed')
    if (filter === 'skipped') return rows.filter((r) => r.phase === 'skipped' || r.phase === 'cancelled')
    return rows
  }, [rows, filter])

  const noteText = (row: FileRow): string | null => {
    if (row.phase === 'needs_decision') {
      return `${row.duplicateCount ?? 0} possible duplicate(s) — your decision needed`
    }
    if (row.phase === 'skipped' && row.wasDuplicate) return 'Duplicate — cancelled by you'
    if (row.phase === 'cancelled') return 'Cancelled before upload'
    // row.failureReason is always plain language by this point -- backend
    // sends it pre-translated (see utils/failure_messages.py), never raw
    // exception/infrastructure text.
    if (row.phase === 'failed' && row.failureReason) return row.failureReason
    if (row.wasDuplicate) return 'Duplicate — uploaded anyway'
    if (row.isDuplicate) {
      const pct = row.duplicateConfidence != null ? ` (${Math.round(row.duplicateConfidence * 100)}%)` : ''
      return `Flagged as possible duplicate${pct}`
    }
    return null
  }

  if (!isOpen) return null

  const FILTERS: { key: FilterKey; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: rows.length },
    { key: 'processing', label: 'In progress', count: counts.queued + counts.inserting },
    { key: 'decision', label: 'Needs decision', count: counts.decision },
    { key: 'matched', label: 'Uploaded', count: counts.uploaded + counts.matched },
    { key: 'failed', label: 'Failed', count: counts.failed },
    { key: 'skipped', label: 'Skipped', count: counts.skipped + counts.cancelled },
  ]

  // ---- Minimized tray ----
  // No backdrop and no full-screen wrapper, so clicks reach the page underneath.
  if (minimized) {
    const total = rows.length || 1
    const pct = Math.round((settled / total) * 100)
    const needsAttention = counts.decision > 0
    return (
      <>
        <div
          className={`fixed bottom-4 right-4 z-50 w-80 rounded-lg border bg-white shadow-2xl ${
            needsAttention ? 'border-amber-400 ring-2 ring-amber-200' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
            <div className="flex items-center gap-2 min-w-0">
              {needsAttention
                ? <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                : busy ? <LoadingSpinner size="sm" /> : <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />}
              <span className="text-sm font-medium text-gray-900 truncate" title="Apply Resume(s)">Apply Resumes</span>
              {jobTitle && <span className="text-xs text-gray-400 truncate">· {jobTitle}</span>}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => setMinimized(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded" title="Expand" aria-label="Expand bulk apply">
                <ArrowsPointingOutIcon className="h-4 w-4" />
              </button>
              <button onClick={handleCloseAll} className="p-1 text-gray-400 hover:text-gray-700 rounded" title="Close" aria-label="Close bulk apply">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="px-3 py-2.5">
            <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${busy ? 'bg-blue-500' : 'bg-green-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-gray-600">
              {busy ? (
                <>
                  {settled} of {rows.length} complete
                  {counts.inserting > 0 && ` · ${counts.inserting} in progress`}
                  {counts.queued > 0 && ` · ${counts.queued} waiting`}
                </>
              ) : (
                <span className="text-green-700">
                  Finished — {counts.uploaded + counts.matched} uploaded
                  {counts.failed > 0 && `, ${counts.failed} failed`}
                  {counts.skipped + counts.cancelled > 0 && `, ${counts.skipped + counts.cancelled} skipped`}
                </span>
              )}
            </p>

            {needsAttention && (
              <button
                onClick={() => setMinimized(false)}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
              >
                <ExclamationTriangleIcon className="h-4 w-4" />
                {counts.decision} need{counts.decision === 1 ? 's' : ''} your decision — review
              </button>
            )}

            {atRiskCount > 0 && (
              <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-blue-800">
                <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                <span>Don't reload — {atRiskCount} not sent yet. Other pages are safe.</span>
              </p>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseAll} />

          <div className={`inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:p-6 ${started ? 'sm:max-w-7xl' : 'sm:max-w-2xl'}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="h-6 w-6 text-green-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Apply Resume(s)</h3>
                  {jobTitle && <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md" title={jobTitle}>{jobTitle}</p>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {started && (
                  <button
                    onClick={() => setMinimized(true)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    title="Minimize — keep uploading while you work elsewhere"
                    aria-label="Minimize bulk apply"
                  >
                    <MinusIcon className="h-5 w-5" />
                  </button>
                )}
                <button onClick={handleCloseAll} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Close bulk apply">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {!started && (
              <>
                <p className="text-xs text-gray-600 mb-4">
                  Resumes are analyzed and automatically applied to this job. If one looks like an existing
                  candidate you'll get the same duplicate confirmation as a single upload — and the rest of the
                  batch keeps going while you decide.
                </p>

                <div className="mb-4">
                  <SourceSelector
                    type="resume"
                    value={source_id}
                    onChange={setSourceId}
                    required={useSameSourceForAll}
                    label={useSameSourceForAll ? 'Source (applies to all Resume(s))' : 'Default source'}
                  />
                  <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={useSameSourceForAll}
                      onChange={(e) => setUseSameSourceForAll(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    Use this source for all Resume(s)
                    <span className="text-gray-400">— untick to set a different source per resume</span>
                  </label>
                </div>

                <div
                  className={`border-2 border-dashed rounded-lg transition-all duration-200 ${
                    entries.length > 0 ? 'p-4' : 'p-8'
                  } ${dragOver ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gray-400'}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={(e) => { e.preventDefault(); setDragOver(false) }}
                  onDrop={handleDrop}
                >
                  <div className="text-center">
                    <CloudArrowUpIcon className={`mx-auto text-gray-400 ${entries.length > 0 ? 'h-8 w-8' : 'h-12 w-12'}`} />
                    <label htmlFor="bulk-apply-resume-upload" className={`cursor-pointer block ${entries.length > 0 ? 'mt-2' : 'mt-4'}`}>
                      <span className={entries.length > 0 ? 'text-sm font-medium text-gray-900' : 'text-lg font-medium text-gray-900'}>
                        Drop resumes here or <span className="text-green-600 hover:text-green-500">browse files</span>
                      </span>
                      <input id="bulk-apply-resume-upload" type="file" multiple className="sr-only" accept={acceptAttr} onChange={handleFileSelect} />
                    </label>
                    <p className="text-sm text-gray-500 mt-2">
                      {allowedTypes.map((t) => t.toUpperCase()).join(', ')} — up to {config.max_file_size_mb}MB each,{' '}
                      <span className="font-medium text-gray-700">Max {maxFiles} Resume(s) per batch</span>
                    </p>
                  </div>
                </div>

                {entries.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {entries.length} of {maxFiles} file{entries.length === 1 ? '' : 's'} ready
                      </span>
                      <button onClick={() => setEntries([])} className="text-xs text-gray-500 hover:text-red-600">Clear all</button>
                    </div>

                    <div className="flex items-center gap-2 px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      <span className="flex-1 min-w-0">Resume</span>
                      <span className="w-1/2 min-w-0">Source</span>
                      <span className="w-5 flex-shrink-0" />
                    </div>

                    <div className={`border border-gray-200 rounded-md divide-y divide-gray-100 ${entries.length > 5 ? 'max-h-52 overflow-y-auto' : ''}`}>
                      {entries.map((entry) => (
                        <div key={entry.key} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate text-gray-800" title={entry.file.name}>{entry.file.name}</span>
                            <span className="text-xs text-gray-400 flex-shrink-0">{formatSize(entry.file.size)}</span>
                          </div>

                          <div className="w-1/2 min-w-0">
                            <select
                              value={useSameSourceForAll ? '' : entry.sourceId}
                              onChange={(e) => setEntrySource(entry.key, e.target.value)}
                              disabled={useSameSourceForAll || sourcesQuery.isLoading}
                              aria-label={`Source for ${entry.file.name}`}
                              className="block w-full rounded-md border-gray-300 py-1 text-xs shadow-sm focus:border-green-500 focus:ring-green-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                            >
                              <option value="">
                                {useSameSourceForAll
                                  ? source_id ? `Same as main — ${sourceName(source_id)}` : 'Same as main source'
                                  : source_id ? `Default — ${sourceName(source_id)}` : 'Select a source'}
                              </option>
                              {sources.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                          </div>

                          <button
                            onClick={() => removeFile(entry.key)}
                            className="text-gray-400 hover:text-red-600 flex-shrink-0"
                            aria-label={`Remove ${entry.file.name}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {!useSameSourceForAll && entriesMissingSource.length > 0 && (
                      <p className="mt-2 text-xs text-amber-700">
                        {entriesMissingSource.length} resume(s) have no source yet — pick one per resume, or set a default above.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-6 flex justify-end space-x-3">
                  <button onClick={handleCloseAll} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                  <button
                    onClick={startBatch}
                    disabled={
                      !entries.length ||
                      (useSameSourceForAll ? !source_id : entriesMissingSource.length > 0)
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Apply Resume(s)
                  </button>
                </div>
              </>
            )}

            {started && (
              <>
                <div className="mb-3 flex items-start justify-between gap-4">
                  <div className="text-sm flex items-center gap-2">
                    {busy ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="text-gray-600">
                          {settled} of {rows.length} complete
                          {counts.inserting > 0 && ` · ${counts.inserting} in progress`}
                          {counts.queued > 0 && ` · ${counts.queued} waiting`}
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        <span className="text-green-700">
                          Finished — {counts.uploaded + counts.matched} uploaded
                          {counts.failed > 0 && `, ${counts.failed} failed`}
                          {counts.skipped + counts.cancelled > 0 && `, ${counts.skipped + counts.cancelled} skipped`}.
                        </span>
                      </>
                    )}
                  </div>
                  {uploadsDispatched && (
                    <p className="text-xs text-gray-500 max-w-sm text-right">
                      <span className="font-medium">Minimize</span> to keep working — you'll be alerted if a
                      duplicate needs your decision.
                    </p>
                  )}
                </div>

                {!busy && counts.uploaded + counts.matched > 0 && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="font-medium">Uploaded doesn't mean analyzed yet.</span> Each resume still
                      needs to be read, scored, and matched against this job, which keeps running in the
                      background even with this window closed. Check the notification bell or the candidates
                      list — a resume moves from Queued to Processing to Analyzed there as it completes.
                    </span>
                  </div>
                )}

                {atRiskCount > 0 && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="font-medium">Don't reload or close the browser</span> — {atRiskCount} resume
                      {atRiskCount === 1 ? '' : 's'} {atRiskCount === 1 ? 'has' : 'have'} not reached the server yet and
                      would need re-uploading. Switching pages inside AI Recruit is safe: this panel stays with you.
                    </span>
                  </div>
                )}

                {counts.decision > 0 && (
                  <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                      <span>
                        <span className="font-semibold">{counts.decision}</span> resume{counts.decision === 1 ? '' : 's'} waiting
                        on your duplicate decision. The rest of the batch is still going.
                      </span>
                    </div>
                    <button
                      onClick={() => setActiveDecisionKey(pendingDecisions[0]?.key ?? null)}
                      className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-white border border-amber-300 rounded-md hover:bg-amber-100 whitespace-nowrap"
                    >
                      Review now
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-3">
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                        filter === f.key
                          ? 'bg-green-600 text-white border-green-600'
                          : f.key === 'decision' && f.count > 0
                            ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-semibold">{f.count}</span> {f.label}
                    </button>
                  ))}
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-md">
                  <div className={filteredRows.length > 5 ? 'max-h-64 overflow-y-auto' : ''}>
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-left text-xs font-medium text-gray-500">
                          <th className="px-3 py-2">Resume File</th>
                          <th className="px-3 py-2">Source</th>
                          <th className="px-3 py-2">Candidate</th>
                          <th className="px-3 py-2">Email</th>
                          <th className="px-3 py-2">Phone</th>
                          <th className="px-3 py-2">Exp</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Notes</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredRows.map((row) => {
                          const note = noteText(row)
                          return (
                            <tr key={row.key} className={row.phase === 'needs_decision' ? 'bg-amber-50/60' : 'hover:bg-gray-50'}>
                              <td className="px-3 py-2 max-w-[14rem]">
                                <div className="flex items-center gap-2 min-w-0">
                                  <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                  <span className="truncate text-gray-800" title={row.file.name}>{row.file.name}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap max-w-[9rem]">
                                <span className="truncate block" title={sourceName(row.sourceId)}>{sourceName(row.sourceId)}</span>
                              </td>
                              <td className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[10rem] truncate" title={row.candidate}>{row.candidate || '—'}</td>
                              <td className="px-3 py-2 text-gray-500 max-w-[12rem]"><span className="truncate block" title={row.email}>{row.email || '—'}</span></td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.phone || '—'}</td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.experience != null ? `${row.experience} yrs` : '—'}</td>
                              <td className="px-3 py-2"><PhasePill phase={row.phase} /></td>
                              <td className="px-3 py-2 max-w-[13rem]">
                                {note ? (
                                  <span
                                    className={`text-xs truncate block ${
                                      row.failureReason ? 'text-red-600' : row.phase === 'needs_decision' ? 'text-amber-800 font-medium' : 'text-amber-700'
                                    }`}
                                    title={note}
                                  >
                                    {note}
                                  </span>
                                ) : (
                                  <span className="text-gray-300 text-xs">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {row.phase === 'needs_decision' && (
                                  <button
                                    onClick={() => setActiveDecisionKey(row.key)}
                                    className="px-2 py-1 text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded hover:bg-amber-200"
                                  >
                                    Review
                                  </button>
                                )}
                                {row.phase === 'matched' && row.resumeId && (
                                  <button onClick={() => openCandidate(row.resumeId)} className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center gap-1" title="View candidate in a new tab">
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" /> View
                                  </button>
                                )}
                                {row.phase === 'failed' && (
                                  <button
                                    onClick={() => retryRow(row)}
                                    className="text-gray-600 hover:text-blue-600 text-xs inline-flex items-center gap-1"
                                    title="Retry"
                                  >
                                    <ArrowPathIcon className="h-4 w-4" /> Retry
                                  </button>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                        {filteredRows.length === 0 && (
                          <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-gray-400">No files in this filter.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between gap-3">
                  <div>
                    {counts.queued > 0 && (
                      <button
                        onClick={handleStopRemaining}
                        className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                        title="Cancel resumes that haven't started. Anything already uploaded keeps processing."
                      >
                        Stop remaining ({counts.queued})
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {busy && (
                      <button
                        onClick={() => setMinimized(true)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors inline-flex items-center gap-1.5"
                        title="Shrink to a corner tray and keep working"
                      >
                        <MinusIcon className="h-4 w-4" />
                        Minimize
                      </button>
                    )}
                    <button onClick={handleCloseAll} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors">
                      {busy ? 'Close' : 'Done'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* The EXACT duplicate popup single upload uses. Opened per duplicate;
          Cancel / Upload and Match Anyway resolve just that one file. */}
      <DuplicateDetectionModal
        isOpen={!!activeDecisionRow}
        onClose={() => activeDecisionRow && decide(activeDecisionRow.key, 'skip')}
        onUploadAnyway={() => activeDecisionRow && decide(activeDecisionRow.key, 'upload')}
        duplicateData={activeDecisionRow?.duplicateData ?? null}
      />
    </>
  )
}

export default BulkApplyResumesModal
